import { forwardRef, InputHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { FieldError } from 'react-hook-form';

type InputElementProps = {
  type: 'email' | 'text' | 'select' | 'checkbox' | 'password' | 'number';
  name: string;
  error?: FieldError;
  handleSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
} & InputHTMLAttributes<HTMLInputElement>;

const inputCva = cva(
  [
    'p-2',
    'text-[16px]',
    'border',
    'w-full',
    'border-slate-300',
    'outline-blue',
    'rounded-md',
    'shadow-sm',
    'focus:outline-none',
    'focus:border-sky-500',
    'focus:ring-sky-500',
    'focus:ring-1',
    'min-h-[45px]',
    'placeholder:text-slate-400',
    'placeholder:text-sm',
    'transition-colors',
    'duration-100',
    'hover:border-border-hover',
  ],
  {
    variants: {
      state: {
        default: '',
        error: '!border-errorColor !outline-errorColor',
        disabled: '!bg-[#F2F2F2]',
      },
    },
  },
);

const InputElement = forwardRef<HTMLInputElement, InputElementProps>(
  (props: InputElementProps, ref: React.Ref<HTMLInputElement>) => {
    const { name, type, error, disabled, handleSubmit, ...rest } = props;
    const inputClassNames = inputCva({ state: disabled ? 'disabled' : error?.message ? 'error' : 'default' });
    return (
      <input
        {...rest}
        className={inputClassNames}
        id={name}
        name={name}
        ref={ref}
        type={type}
        disabled={disabled}
        onKeyDown={(e) => {
          // @ts-expect-error Mismatch between the type of the event and the expected type
          if (e.key === 'Enter' && !error) handleSubmit(e);
        }}
      />
    );
  },
);

InputElement.displayName = 'InputElement';

export default InputElement;
